<template>
  <v-card class="mt-5" outlined>
    <v-card-title>
      Operations Overview
      <v-spacer></v-spacer>
      <span v-if="dashboard" class="caption grey--text mr-3">
        {{ dashboard.month }} &middot; generated {{ dashboard.generated_at }}
      </span>
      <v-btn small icon :loading="loading" @click="refresh">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text v-if="dashboard">
      <!-- ============ 1. REPAIR SNAPSHOT (Part 10) ============ -->
      <h3 class="mb-2">Repair Snapshot</h3>
      <v-row>
        <v-col
          cols="12"
          md="6"
          v-for="branch in dashboard.branches"
          :key="'repairs-' + branch.branch_id"
        >
          <v-card outlined>
            <v-card-subtitle class="font-weight-bold pb-1">{{
              branch.branch_name
            }}</v-card-subtitle>
            <v-card-text>
              <div class="mb-3">
                <v-chip
                  v-for="(count, status) in branch.repairs.status_counts"
                  :key="status"
                  small
                  label
                  class="mr-1 mb-1"
                  :color="statusColor(status)"
                  text-color="white"
                >
                  {{ status }}: {{ count }}
                </v-chip>
              </div>

              <v-row dense class="text-center mb-2">
                <v-col
                  cols="3"
                  v-for="stat in monthStats(branch)"
                  :key="stat.label"
                >
                  <div class="text-h6">{{ stat.value }}</div>
                  <div class="caption grey--text">{{ stat.label }}</div>
                </v-col>
              </v-row>

              <div class="mb-3">
                <div class="caption font-weight-bold">
                  Top not-successful reasons ({{ dashboard.month }})
                </div>
                <div
                  v-if="branch.repairs.top_outcome_reasons.length <= 0"
                  class="caption grey--text"
                >
                  No not-successful outcomes recorded yet.
                </div>
                <div
                  v-for="reason in branch.repairs.top_outcome_reasons"
                  :key="reason.outcome_reason"
                  class="caption"
                >
                  {{ reason.outcome_reason }} ({{ reason.total }})
                </div>
              </div>

              <div class="caption font-weight-bold mb-1">Technician load</div>
              <v-simple-table
                dense
                v-if="branch.repairs.technician_load.length > 0"
              >
                <thead>
                  <tr>
                    <th>Technician</th>
                    <th class="text-right">Jobs</th>
                    <th class="text-right">Done</th>
                    <th class="text-right">Rate</th>
                    <th class="text-right">Avg days</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="tech in branch.repairs.technician_load"
                    :key="tech.technician"
                  >
                    <td>{{ tech.technician }}</td>
                    <td class="text-right">{{ tech.total_problems }}</td>
                    <td class="text-right">{{ tech.completed }}</td>
                    <td class="text-right">{{ tech.completion_rate }}%</td>
                    <td class="text-right">
                      {{
                        tech.avg_turnaround_days !== null
                          ? tech.avg_turnaround_days
                          : "—"
                      }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
              <div v-else class="caption grey--text">
                No repairs received this month.
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-divider class="my-4"></v-divider>

      <!-- ============ 2. FACILITIES COMPLIANCE (Part 9) ============ -->
      <h3 class="mb-2">Facilities Compliance</h3>
      <v-row>
        <v-col
          cols="12"
          md="6"
          v-for="branch in dashboard.branches"
          :key="'facilities-' + branch.branch_id"
        >
          <v-card outlined>
            <v-card-subtitle class="font-weight-bold pb-1">{{
              branch.branch_name
            }}</v-card-subtitle>
            <v-card-text>
              <div
                v-for="check in facilityChecks(branch)"
                :key="check.label"
                class="d-flex align-center mb-1"
              >
                <span
                  class="body-2"
                  :class="check.overdue ? 'red--text font-weight-bold' : ''"
                >
                  {{ check.label }} — last:
                  {{ check.last || "never" }}
                </span>
                <v-chip
                  v-if="check.overdue"
                  x-small
                  color="red"
                  text-color="white"
                  class="ml-2"
                >
                  Overdue
                </v-chip>
              </div>

              <div class="caption font-weight-bold mt-3 mb-1">
                Flagged equipment (latest check)
              </div>
              <div
                v-if="branch.facilities.flagged_equipment.length <= 0"
                class="caption grey--text"
              >
                All equipment in good order.
              </div>
              <div
                v-for="item in branch.facilities.flagged_equipment"
                :key="item.name"
                class="d-flex align-center caption mb-1"
              >
                <span>{{ item.name }}</span>
                <v-chip
                  x-small
                  class="mx-1"
                  :color="item.status === 'missing' ? 'grey' : 'orange'"
                  text-color="white"
                >
                  {{ item.status.replace("_", " ") }}
                </v-chip>
                <span v-if="item.remarks" class="grey--text">{{
                  item.remarks
                }}</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-text v-else class="text-center grey--text">
      {{ loading ? "Loading operations overview..." : "No data available." }}
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <p class="caption grey--text text-right mb-0">
        vpOperationsDashboard.vue
      </p>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
export default {
  data() {
    return {
      status_colors: {
        Pending: "orange",
        "Work on Process": "blue",
        "Ready to Pick-up": "green",
        "Picked Up": "teal",
        Unfixed: "red",
        Cancelled: "grey",
      },
    };
  },
  created() {
    this.refresh();
  },
  computed: {
    ...mapGetters({
      dashboard: "vp_operations_dashboard/dashboard",
      loading: "vp_operations_dashboard/loading",
    }),
  },
  methods: {
    ...mapActions({
      fetch_dashboard: "vp_operations_dashboard/fetch_dashboard",
    }),
    async refresh() {
      try {
        await this.fetch_dashboard();
      } catch (error) {
        var status = error.response?.status;
        var serverMessage = error.response?.data?.message;
        this.$alert({
          title: "Error",
          message:
            "Unable to load the operations overview." +
            (status ? " (HTTP " + status + ")" : "") +
            (serverMessage ? " " + serverMessage : ""),
          color: "red",
          icon: "$error",
        });
      }
    },
    statusColor(status) {
      return this.status_colors[status] || "grey";
    },
    monthStats(branch) {
      return [
        { label: "Received", value: branch.repairs.month.received },
        { label: "Finished", value: branch.repairs.month.finished },
        { label: "Successful", value: branch.repairs.month.successful },
        { label: "Not-successful", value: branch.repairs.month.not_successful },
      ];
    },
    facilityChecks(branch) {
      return [
        {
          label: "Tools & Equipment Check",
          last: branch.facilities.last_equipment_check,
          overdue: branch.facilities.equipment_check_overdue,
        },
        {
          label: "Daily Facilities Check",
          last: branch.facilities.last_daily_check,
          overdue: branch.facilities.daily_check_overdue,
        },
      ];
    },
  },
};
</script>

<style></style>
